import {useCallback, useEffect, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import clsx from 'clsx';
import {SoopInputBox, SoopDropdownItem} from '@soop-ui/soop-ui';
import styles from '@/components/help/common/FormContainer/FormContainer.module.scss';
import {ControllerWrapper} from '@/components/common';
import {EMAIL_LIST} from '@/constants/help/form';
import {useAutoCompleteNavigation, useDropdown} from '@/hooks/help';
import {
  splitEmail,
  isEmailIdValid,
  isPartialEmail,
  isFullEmail,
  shouldOpenDropdown,
} from '@/utils/help/formUtils';
import {FORM_FIELDS} from '@/constants/help/form';
import {useFormClear} from '@/hooks/help/useFormClear';
import {useFormKeyHandler} from '@/hooks/help/useFormKeyHandler';

const EMAIL_ERROR_MESSAGE =
  '이메일 형식이 올바르지 않습니다. 다시 확인해 주세요.';

const Email = () => {
  const {
    watch,
    setValue,
    trigger,
    clearErrors,
    setError,
    getFieldState,
    formState: {errors},
  } = useFormContext();
  const {handleClear} = useFormClear();
  const {handleFormKeyDown} = useFormKeyHandler();
  const {isDropdownOpen, dropdownAreaRef, openDropdown, closeDropdown} =
    useDropdown();

  const onClear = () => handleClear(FORM_FIELDS.EMAIL, {shouldFocus: true});

  const emailValue = watch(FORM_FIELDS.EMAIL);

  const emailIdPart = emailValue?.split('@')[0] || '';
  const dynamicEmailList = useMemo(
    () =>
      EMAIL_LIST.map((domain) => ({
        label: `${emailIdPart}@${domain.label}`,
        value: domain.value,
      })),
    [emailIdPart],
  );

  const handleSelect = useCallback(
    (domain) => {
      setValue(FORM_FIELDS.EMAIL, `${emailIdPart}@${domain}`, {
        shouldValidate: true,
      });
      clearErrors(FORM_FIELDS.EMAIL);
    },
    [emailIdPart, setValue, clearErrors],
  );

  const {listWrapperProps, listRefs, selectedIndex, onInputKeyDown} =
    useAutoCompleteNavigation<HTMLDivElement>({
      list: dynamicEmailList,
      onEnterKeyUp: (item) => handleSelect((item as {value: string}).value),
      onESCKeyUp: closeDropdown,
    });

  const checkDropdownOpen = () => {
    if (shouldOpenDropdown(emailValue)) {
      openDropdown();
    } else {
      closeDropdown();
    }
  };

  useEffect(() => {
    checkDropdownOpen();

    const handleOutsideClick = (e: MouseEvent) => {
      // 컨테이너 영역(인풋 + 드롭다운) 밖을 클릭한 경우
      if (
        dropdownAreaRef.current &&
        !dropdownAreaRef.current.contains(e.target as Node)
      ) {
        const {isTouched} = getFieldState(FORM_FIELDS.EMAIL);

        if (!isTouched) {
          return;
        }
        // 'xxx@' 처럼 부분 이메일이면 강제로 에러를 띄움
        if (!isFullEmail(emailValue)) {
          setError(FORM_FIELDS.EMAIL, {
            type: 'pattern',
            message: EMAIL_ERROR_MESSAGE,
          });
        } else {
          // 그 외에는 일반 검증 실행
          trigger(FORM_FIELDS.EMAIL);
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [emailValue, trigger, setError, getFieldState]);

  const handleKeyDown = (e) => {
    handleFormKeyDown(e);
    onInputKeyDown(e);
  };

  return (
    <div className={styles.section} ref={dropdownAreaRef}>
      <label className={styles.label}>
        이메일 주소 <em>*</em>
      </label>
      <ControllerWrapper
        component={SoopInputBox}
        needsBlurWrapper={true}
        clearable={true}
        size='44'
        name={FORM_FIELDS.EMAIL}
        rules={{
          required: EMAIL_ERROR_MESSAGE,
          validate: (value: string) => {
            const {emailId} = splitEmail(value);

            // 허용하지 않는 문자
            if (!isEmailIdValid(emailId)) {
              return EMAIL_ERROR_MESSAGE;
            }

            // 부분 입력(abc@)이면 통과
            if (isPartialEmail(value)) {
              return true;
            }

            // 전체 이메일 최종 검사
            return isFullEmail(value) || EMAIL_ERROR_MESSAGE;
          },
        }}
        placeholder="'@' 포함 이메일 주소 입력"
        error={!!errors[FORM_FIELDS.EMAIL]}
        helperStatus={errors[FORM_FIELDS.EMAIL] ? 'error' : undefined}
        helperText={errors[FORM_FIELDS.EMAIL]?.message as string}
        onClear={onClear}
        onFocus={checkDropdownOpen}
        onKeyDown={handleKeyDown}
        autoComplete='off'
      />

      {isDropdownOpen && (
        <div
          className={clsx(
            styles.DropdownList,
            errors.emailId ? styles.ErrorLayout : '',
          )}
          {...listWrapperProps}>
          {dynamicEmailList.map((item, idx) => (
            <SoopDropdownItem
              key={item.value}
              id={item.value}
              size={44}
              mode='default'
              label={item.label}
              value={item.value}
              className={selectedIndex === idx ? styles.selected : undefined}
              onClick={handleSelect}
              ref={(el) => {
                listRefs.current[idx] = el;
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Email;
